import { useState } from 'react';
import { faCheck, faChevronLeft, faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NSSwitch } from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import { PERIOD_OPTIONS, Periods } from 'components/ProFormaReturnsTable/constant';
import RenderIf from 'components/RenderIf/RenderIf';
import { ProFormaModelIds } from 'constants/proForma';
import { DropdownItem } from 'reactstrap';
import { useProFormaSocketContext } from 'views/ProFormaTable/components/ProForma/socketContext/ProFormaSocketProvider';
import {
    HIDE_WARNINGS_KEY, ProFormaSideNavSettingsMenuKeys, ProFormaSideNavSettingsMenuLabels,
    SELECTED_PERIOD_CADENCE_KEY, WATTS_OPTIONS, WATTS_TYPE,
} from 'views/ProFormaTable/constants';
import './ProFormaSideNavSettingsMenu.scss';
import NSSettingsMenu from 'bricks/NSSettingsMenu/NSSettingsMenu';

export interface IProFormaSideNavSettingsMenuProps {
    handleToggleShowPartnerReturns: () => void;
    showPartnerReturns: boolean;
}

const ProFormaSideNavSettingsMenu = ({
    handleToggleShowPartnerReturns, showPartnerReturns,
}: IProFormaSideNavSettingsMenuProps) => {
    const {
        proForma, showCents,
        handleToggleShowCents, getLocalStorageValueByKey,
        updateLocalStorageByKey, wattsType,
    } = useProFormaSocketContext();

    const [childMenuType, setChildMenuType] = useState<ProFormaSideNavSettingsMenuKeys | null>(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const localStoragePeriodCadence = getLocalStorageValueByKey(SELECTED_PERIOD_CADENCE_KEY) || PERIOD_OPTIONS[0].value;
    const localStorageHideWarnings = Boolean(getLocalStorageValueByKey(HIDE_WARNINGS_KEY));

    const toggleMenu = () => {
        if (isMenuOpen) setChildMenuType(null);
        setIsMenuOpen(!isMenuOpen);
    };

    const handleSelectPeriod = (period: IOptionAnyValue) => updateLocalStorageByKey(SELECTED_PERIOD_CADENCE_KEY, period.value);

    const handleSelectWattsType = (wattsType: IOptionAnyValue) => updateLocalStorageByKey(WATTS_TYPE, wattsType.value);

    const handleToggleHideWarnings = () => updateLocalStorageByKey(HIDE_WARNINGS_KEY, !localStorageHideWarnings);

    const BackButton = () => (
        <DropdownItem
            toggle={false}
            className="d-flex align-items-center text-primary"
            onClick={() => setChildMenuType(null)}
        >
            <FontAwesomeIcon icon={faChevronLeft} size="sm" className="mr-1" />
            <span>Back</span>
        </DropdownItem>
    );

    const renderChildMenuOptions = (
        options: IOptionAnyValue[],
        selectedValue: any,
        onSelect: (option: IOptionAnyValue) => void,
    ) => (
        <>
            {options.map(option => (
                <DropdownItem
                    key={option.value}
                    className="d-flex justify-content-between align-items-center"
                    onClick={() => onSelect(option)}
                >
                    <span>{option.label}</span>
                    <RenderIf isTrue={selectedValue === option.value}>
                        <FontAwesomeIcon icon={faCheck} size="sm" className="text-primary" />
                    </RenderIf>
                </DropdownItem>
            ))}
        </>
    );

    const filteredPeriodOptions = PERIOD_OPTIONS.filter(option => option.value !== Periods.QUARTERLY);

    const SETTINGS_MENU_ITEMS = [
        {
            key: ProFormaSideNavSettingsMenuKeys.PERIOD_CADENCE,
            label: ProFormaSideNavSettingsMenuLabels.PERIOD_CADENCE,
            visible: true,
            onClick: () => setChildMenuType(ProFormaSideNavSettingsMenuKeys.PERIOD_CADENCE),
            rightComponent: (
                <FontAwesomeIcon icon={faChevronRight} size="sm" />
            ),
            children: (
                <>
                    <BackButton />
                    {renderChildMenuOptions(filteredPeriodOptions, localStoragePeriodCadence, handleSelectPeriod)}
                </>
            ),
        },
        {
            key: ProFormaSideNavSettingsMenuKeys.WATTS,
            label: ProFormaSideNavSettingsMenuLabels.WATTS,
            visible: proForma.proFormaModelId === ProFormaModelIds.DATA_CENTER,
            onClick: () => setChildMenuType(ProFormaSideNavSettingsMenuKeys.WATTS),
            rightComponent: (
                <FontAwesomeIcon icon={faChevronRight} size="sm" />
            ),
            children: (
                <>
                    <BackButton />
                    {renderChildMenuOptions(WATTS_OPTIONS, wattsType, handleSelectWattsType)}
                </>
            ),
        },
        {
            key: ProFormaSideNavSettingsMenuKeys.HIDE_WARNINGS,
            label: ProFormaSideNavSettingsMenuLabels.HIDE_WARNINGS,
            visible: Boolean(proForma.boeScenarioId),
            rightComponent: (
                <NSSwitch
                    id="hideDiscrepancyWarningsToggle"
                    name="hideDiscrepancyWarningsToggle"
                    checked={localStorageHideWarnings}
                    onChange={handleToggleHideWarnings}
                    containerClassName="text-dark heap-deal-pro-forma-settings-hide-boe-warning"
                />
            ),
        },
        {
            key: ProFormaSideNavSettingsMenuKeys.SHOW_CENTS,
            label: ProFormaSideNavSettingsMenuLabels.SHOW_CENTS,
            visible: true,
            rightComponent: (
                <NSSwitch
                    id="showCentsToggle"
                    name="showCentsToggle"
                    checked={showCents}
                    onChange={handleToggleShowCents}
                    containerClassName="text-dark heap-deal-pro-forma-settings-show-cents"
                />
            ),
        },
        {
            key: ProFormaSideNavSettingsMenuKeys.SHOW_PARTNER_RETURNS,
            label: ProFormaSideNavSettingsMenuLabels.SHOW_PARTNER_RETURNS,
            visible: true,
            rightComponent: (
                <NSSwitch
                    id="showPartnerReturnsToggle"
                    name="showPartnerReturnsToggle"
                    checked={showPartnerReturns}
                    onChange={handleToggleShowPartnerReturns}
                    containerClassName="text-dark heap-deal-pro-forma-settings-show-partner-returns"
                />
            ),
        },
    ];

    return (
        <NSSettingsMenu
            isMenuOpen={isMenuOpen}
            toggleMenu={toggleMenu}
            menuItems={SETTINGS_MENU_ITEMS}
            childMenuType={childMenuType}
        />
    );
};

export default ProFormaSideNavSettingsMenu;
